// پیشنهادِ ثبت‌نام به مهمان — WHOSUP-308. قرینه‌ی
// apps/mobile/src/features/signup/SignupGate.tsx.
//
// تا پیش از این، کارِ حساب‌دارِ مهمان (ذخیره، «کی پایه‌ست؟») او را بی‌هیچ
// توضیحی به صفحه‌ی ورود پرت می‌کرد. حالا اول برگه‌ای می‌آید که می‌گوید
// **چرا**، و «بعداً» دارد؛ اگر عضو شد، `state.from` او را به همین صفحه
// برمی‌گرداند تا همان کار را تمام کند.
//
// قانونِ «کِی نوار» و متن‌ها در core/lib/signupNudge.ts است، مشترک با اپ.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@/core/session';
import { t } from '@/core/lib/i18n';
import { trackSignupPrompt } from '@/core/lib/analytics';
import {
  GATE_REASON_KEY, JOIN_BENEFIT_KEYS, takeNudge, type GateReason, type NudgeTrigger,
} from '@/core/lib/signupNudge';
import { Btn, Card } from '@/ui/kit';
import { returnHere } from '@/features/auth/returnHere';

type Open = (reason: GateReason) => void;
const GateCtx = createContext<Open | null>(null);

/** یک برگه برای کلِ سایت — در `app/App.tsx`، زیرِ روتر. */
export function SignupGateProvider({ children }: { children: React.ReactNode }) {
  const [reason, setReason] = useState<GateReason | null>(null);
  const nav = useNavigate();
  const location = useLocation();

  const open = useCallback<Open>((r) => {
    trackSignupPrompt('gate', 'shown', r);
    setReason(r);
  }, []);

  const later = () => {
    if (reason) trackSignupPrompt('gate', 'later', reason);
    setReason(null);
  };
  const signup = () => {
    if (!reason) return;
    trackSignupPrompt('gate', 'signup', reason);
    setReason(null);
    nav('/auth', { state: returnHere(location) });
  };

  return (
    <GateCtx.Provider value={open}>
      {children}
      {reason ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={later}>
          <Card className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('joinTitle')}</h2>
            <p>{t(GATE_REASON_KEY[reason])}</p>
            <ul className="dim">
              {JOIN_BENEFIT_KEYS.map((k) => <li key={k}>{t(k)}</li>)}
            </ul>
            <Btn label={t('joinCta')} block onClick={signup} testId="gate-signup" />
            <Btn label={t('later')} kind="ghost" block onClick={later} testId="gate-later" />
          </Card>
        </div>
      ) : null}
    </GateCtx.Provider>
  );
}

/**
 * «این کار حساب می‌خواهد.» واردشده: همان کار همین حالا. مهمان: برگه.
 *
 * `loading` جدا گرفته می‌شود و این عمدی است — همان تجربه‌ی `favorites.tsx`:
 * نشست چند صدم ثانیه بعد از اولین رندر می‌نشیند و کلیکِ همان فاصله نه باید
 * کار را بکند و نه برگه‌ی «عضو شو» به کسی نشان بدهد که عضو است.
 */
export function useAccountGate(): (reason: GateReason, go: () => void) => void {
  const { session, loading } = useSession();
  const open = useContext(GateCtx);
  const nav = useNavigate();
  const location = useLocation();
  return useCallback((reason, go) => {
    if (session) { go(); return; }
    if (loading) return;
    if (open) open(reason);
    else nav('/auth', { state: returnHere(location) });
  }, [session, loading, open, nav, location]);
}

/**
 * یادآوریِ ملایم: نوارِ پایینِ صفحه‌ی کشف، فقط مهمان، فقط وقتی `takeNudge`
 * بگوید — بعد از رفتار، حداکثر سه بار، با «بعداً».
 */
export function NudgeBar() {
  const { session, loading } = useSession();
  const [why, setWhy] = useState<NudgeTrigger | null>(null);
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading || session) return;
    let alive = true;
    void takeNudge().then((due) => {
      if (!alive || !due) return;
      trackSignupPrompt('nudge', 'shown', due);
      setWhy(due);
    });
    return () => { alive = false; };
  }, [loading, session]);

  if (!why || session) return null;
  return (
    <div className="nudge-bar" role="status">
      <span>{t('nudgeBody')}</span>
      <Btn label={t('later')} kind="ghost" testId="nudge-later"
        onClick={() => { trackSignupPrompt('nudge', 'later', why); setWhy(null); }} />
      <Btn label={t('joinCta')} testId="nudge-signup"
        onClick={() => {
          trackSignupPrompt('nudge', 'signup', why);
          setWhy(null);
          nav('/auth', { state: returnHere(location) });
        }} />
    </div>
  );
}
